/* eslint-disable max-statements */
import D7ViewProspects from './view_prospects/d7/view_prospects';
import { DeleteOutlineRounded } from '@mui/icons-material';
import DeleteProspectModal from './modal/delete_prospect';
import FetchDataModal from './modal/fetching_data';
import FindD7ProspectsModal from './modal/find_d7_prospects';
import FindLSProspectsModal from './modal/find_ls_prospects';
import HeaderMapper from './header_mapper';
import ProspectHeaderComponent from './prospect_list_header';
import { ProspectObject } from '@params/prospect';
import styled from 'styled-components';
import UploadProspectsModal from './modal/upload_prospects';
import ViewLSProspects from './view_prospects/ls/view_prospects';
import ViewProspectsModal from './modal/view_prospects';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, Card, CardContent, CircularProgress, IconButton, Typography } from '@mui/material';
import { fetchSuggestionsThunk, getProspectDetailThunk, getProspectsThunk } from '../../redux/thunks/prospect';
import React, { useEffect, useState } from 'react';
import { settingProspectDetail, settingSelectedProspect } from '../../redux/slices/prospect';
import { useDispatch, useSelector } from 'react-redux';

export const Overlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100vw',
  zIndex: 10
});

const ProspectComponent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [sort, setSort] = useState<boolean>(false);
  const [uploadProspects, setUploadProspects] = useState(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [fetchingData, setFetchingData] = useState<string | null>(null);
  const [findLSProspects, setFindLSProspects] = useState(false);
  const [findD7Prospects, setFindD7Prospects] = useState(false);
  const [isProspectsOpen, setIsProspectOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState<ProspectObject | null>(null);
  const { prospects, loaders, manual, selectedProspect: selectedProspectState } = useSelector((state: RootState) => state.prospect);
  const sortedProspects = [...prospects].sort((a, b) => {
    return sort
      ? a.niche.localeCompare(b.niche)
      : b.niche.localeCompare(a.niche);
  });

  const fetchProspects = async () => {
    try {
      if (initialLoad) {
        // await resetStatesExceptAuth(dispatch);
        await dispatch(getProspectsThunk()).unwrap();
        setInitialLoad(false);
      } else {
        await dispatch(getProspectsThunk()).unwrap();
      }
    } catch (e) {
      //
    }
  };

  useEffect(() => {
    fetchProspects();
    const interval = setInterval(() => {
      dispatch(getProspectsThunk());
    }, 120000);

    // ✅ Cleanup interval when component unmounts
    return () => clearInterval(interval);
  }, [dispatch]);

  const handleViewProspects = async (prospect: ProspectObject) => {
    setInitialLoad(true);
    dispatch(settingSelectedProspect(prospect));
    try {
      await dispatch(getProspectDetailThunk()).unwrap();
      setIsProspectOpen(true);
      setInitialLoad(false);
    } catch (e) {
      // Catch errors
    }
    if (selectedProspect) setSelectedProspect(null);
  };

  const handleDelete = (prospect: ProspectObject) => {
    dispatch(settingSelectedProspect(prospect));
    setDeleteModal(true);
  };

  // const readyListOfProspectHandler = async () => {
  //   try {
  //     // await dispatch(fetchProspectThunk()).unwrap();
  //     await dispatch(getProspectsThunk()).unwrap();
  //   } catch (e) {
  //     // Catch errors
  //   }
  // };

  const searchProspectsHandler = async (value: 'ls' | 'd7') => {
    try {
      await dispatch(fetchSuggestionsThunk()).unwrap();
      if (value === 'ls') {
        setFindLSProspects(true);
      } else if (value === 'd7') {
        setFindD7Prospects(true);
      }
    } catch (e) {
      //
    }
  };

  if (initialLoad) {
    return (<Overlay>
      <CircularProgress size={24} />
      <Typography ml={2}>Loading...</Typography>
    </Overlay>);
  }

  if (manual.isManual) {
    return <HeaderMapper />;
  }

  if (isProspectsOpen && selectedProspectState?.type === 'd7') {
    return <D7ViewProspects setIsProspectOpen={() => {
      setIsProspectOpen(false);
      dispatch(settingProspectDetail(null));
    }} />;
  } else if (isProspectsOpen && selectedProspectState?.type === 'ls') {
    return <ViewLSProspects setIsProspectOpen={() => {
      setIsProspectOpen(false);
      dispatch(settingProspectDetail(null));
    }} />;
  }

  return (
    <Box p={1} >
      {/* Header */}
      <ProspectHeaderComponent
        setUploadProspects={(val) => setUploadProspects(val)}
        searchProspectsHandler={(val: 'ls' | 'd7') => searchProspectsHandler(val)}
        prospectLength={prospects.length}
        sort={sort}
        setSort={(val) => setSort(val)}
        loading={initialLoad}
      />
      <Box minHeight="65vh" overflow="auto" maxHeight="65vh">
        {sortedProspects.map((prospect, index) => (
          <Card key={index} variant="outlined" sx={{ mb: 2 }}>
            <CardContent
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight="bold" color="rgba(51,56,63,1)">
                  {prospect.niche} – {prospect.type === 'ls' ? prospect.address : prospect.city}
                </Typography>
              </Box>
              {prospect.status === 'completed'
                ? <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" fontWeight="bold" color="rgba(111,118,126,1)">
                    {prospect.total_results} Prospects
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    disabled={loaders.isSearching}
                    onClick={() => handleViewProspects(prospect)}
                  >
                    View Prospects
                  </Button>
                  <IconButton
                    color="error"
                    disabled={loaders.isSearching}
                    onClick={() => handleDelete(prospect)}
                  >
                    <DeleteOutlineRounded fontSize="small" color="error" />
                  </IconButton>
                </Box>
                : <Box>
                  {/* prospect?.wait_time === 0
                ? <Box>
                  <Typography>Processing Please wait...</Typography>
                </Box>
                :  */}
                  <Typography>Your list will be ready in 5 mins.
                    {/* <TimeRemaining
                    time={prospect.wait_time || 5}
                    onTimeExpired={() => readyListOfProspectHandler()}
                  /> */}
                  </Typography>
                </Box>
              }
            </CardContent>
          </Card>
        ))}
      </Box>
      {uploadProspects && <UploadProspectsModal open={uploadProspects} onClose={() => setUploadProspects(false)} />}
      {findLSProspects && <FindLSProspectsModal open={findLSProspects} onClose={() => setFindLSProspects(false)} onSubmit={(value: string) => setFetchingData(value)} />}
      {findD7Prospects && <FindD7ProspectsModal open={findD7Prospects} onClose={() => setFindD7Prospects(false)} onSubmit={(value: string) => setFetchingData(value)} />}
      <FetchDataModal open={fetchingData !== null} onClose={() => setFetchingData(null)} name={fetchingData || 'unknown'} />
      {deleteModal && <DeleteProspectModal open={deleteModal} onClose={() => setDeleteModal(false)} />}
      {selectedProspect && (
        <ViewProspectsModal
          open={Boolean(selectedProspect)}
          selectedProspect={selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onView={() => handleViewProspects(selectedProspect)}
        />
      )}
    </Box>
  );
};

export default ProspectComponent;
